/**
 * A mini trend (a recharts AreaChart, no axes): a 1.5px line over a faint
 * fill of the same color, neutral ink by default or a category's mark color.
 * Nulls are gaps. Decorative unless given a `label`; pair it with the figure
 * it summarizes.
 */
import { useMemo } from 'react'
import clsx from 'clsx'
import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts'
import type { Category } from './types'
import { useChartTheme } from './useChartTheme'

export interface SparklineProps {
  points: Array<number | null>
  category?: Category
  /** Height in px. Default 32. */
  height?: number
  /** Accessible name ("Drifts per day, last 30 days: 12 to 4"). */
  label?: string
  className?: string
}

export function Sparkline({ points, category, height = 32, label, className }: SparklineProps) {
  const theme = useChartTheme()
  const color = category ? theme.series[category] : theme.neutral
  const data = useMemo(() => points.map((y, i) => ({ i, y })), [points])
  return (
    <div
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      className={clsx('min-w-0', className)}
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 1, left: 0 }}>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Area
            type="linear"
            dataKey="y"
            stroke={color}
            strokeWidth={1.5}
            fill={color}
            fillOpacity={0.12}
            dot={false}
            activeDot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default Sparkline
